import React from 'react';
import VehiclePathDisplay from './VehiclePathDisplay';

// Format path name display helper
const getPathName = (path, locations) => {
  if (!path || path.length === 0) return "Undefined path";
  
  return path.map(id => {
    const loc = locations.find(l => l.id === id);
    return loc ? loc.name : id;
  }).join(" → ");
};

// Validate path data and identify missing information
const validatePathData = (pathInfo, path) => {
  if (!pathInfo) return { isValid: false, errorMessage: "Path information is missing" };
  
  // Check for essential path properties
  if (typeof pathInfo.distance !== 'number') {
    return { isValid: false, errorMessage: "Path distance information is missing" };
  }
  
  if (typeof pathInfo.pointCount !== 'number') {
    return { isValid: false, errorMessage: "Path point count information is missing" };
  }
  
  if (!path || !Array.isArray(path) || path.length < 2) {
    return { isValid: false, errorMessage: "Path must contain at least two points" };
  }
  
  return { isValid: true, errorMessage: null };
};

// Create single vehicle path display function as a regular function to avoid React Hook rules issues
const createSingleVehiclePath = (path, locations, distance, className) => {
  if (!path || !Array.isArray(path) || path.length < 2) {
    return null;
  }
  
  // Use orange color for custom path, blue for auto path
  const color = className === 'custom' ? '#FF5722' : '#4285F4';
  
  return [{
    id: 1,
    vehicle_id: 1,
    color: color,
    distance: distance,
    path: path
  }];
};

const PathInfo = React.memo(function PathInfo({ pathInfo, path, locations, title, className, isEmpty }) {
  // Validate path data
  const { isValid, errorMessage } = validatePathData(pathInfo, path);
  
  // If we're in the custom path section and have a path but no vehicle paths,
  // we should create mock vehicle paths for display
  // IMPORTANT: With unified reading approach, we should NOT create fallback paths anymore
  // because all paths should have vehiclePaths data when set properly
  const needsVehiclePaths = false; // Disabled fallback to avoid merging paths
  
  // Cache path information to avoid unnecessary recalculations
  const vehiclePaths = React.useMemo(() => {
    if (!isValid || !pathInfo) return null;
    
    if (pathInfo.vehiclePaths && pathInfo.vehiclePaths.length > 0) {
      return pathInfo.vehiclePaths;
    } else if (needsVehiclePaths) {
      return createSingleVehiclePath(path, locations, pathInfo.distance, className);
    }
    return null;
  }, [isValid, pathInfo, needsVehiclePaths, path, locations, className]);
  
  return (
    <div className={`path-info ${className || ''} ${!isValid ? 'error' : (!pathInfo ? 'empty' : '')}`}>
      <h5>{title}</h5>
      {isValid && pathInfo ? (
        <>
          <p className="distance-value">{pathInfo.distance.toFixed(2)} km</p>
          <p>Contains {pathInfo.pointCount} points</p>
          <p>Vehicle Count: {pathInfo.vehicleCount || 'Unknown'}</p>
          
          {/* Use VehiclePathDisplay for both multi-vehicle and single-vehicle paths */}
          {vehiclePaths ? (
            // Use cached path data
            <VehiclePathDisplay 
              vehiclePaths={vehiclePaths} 
              locations={locations} 
            />
          ) : className === 'custom' ? (
            // For custom paths without vehicle data, show helpful message
            <div className="no-vehicle-data">
              <p className="info-text">Vehicle path details are not available for this custom path.</p>
              <p className="hint-text">Please recreate the path in Custom Path mode to see individual vehicle routes.</p>
            </div>
          ) : (
            // For auto paths, show error message
            <p className="no-path-data error-text">No detailed path data available - vehicle path information is missing</p>
          )}
        </>
      ) : (
        <>
          <p className="error-message">{errorMessage || `No ${isEmpty} path data`}</p>
          {errorMessage && <p className="error-hint">Please go to {className === 'auto' ? 'Auto Find Path' : 'Custom Path'} mode to create a valid path.</p>}
        </>
      )}
    </div>
  );
});

export default PathInfo; 